import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';

import { Form } from 'core/forms';
import { Button } from 'uikit/buttons';
import { Progress } from 'uikit/progress';
import { AnimatePresence, motion } from 'framer-motion';

import styles from '../styles/formFieldUpload.module.scss';

const FormFieldUploadActions = ({
  altTextLabel = null,
  buttonSize = 'medium',
  fieldId,
  handleAltTextChange,
  handleFileUpload,
  handleRemoveUpload,
  hasAltTextInput = false,
  hasIcon = false,
  hasRemoveUploadButton = false,
  hasReplaceUploadButton,
  icon = '',
  iconPosition = 'right',
  isDisabled = false,
  permittedFormats = '',
  removeUploadButtonText = '',
  replaceUploadButtonText = '',
  upload = { progress: 0, status: null, url: null },
  uploadButtonText,
}) => {
  const altTextLabelText = altTextLabel || i18n.t('upload-alt-text');
  const fileUploadInput = useRef();

  const renderReplaceUploadButton = () => {
    if (hasReplaceUploadButton && upload.status !== 'uploading') {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="forms__form-field-upload-actions-replace"
        >
          <input
            type="file"
            id={`file-upload-${fieldId}`}
            onChange={(event) => handleFileUpload(event.target.files[0])}
            hidden
            className={styles.input}
            accept={permittedFormats}
            ref={fileUploadInput}
          />
          <Button
            onClick={() => fileUploadInput.current.click()}
            size={buttonSize}
            variant="outlined"
            icon={hasIcon ? icon : ''}
            iconPosition={iconPosition}
            text={upload.url ? replaceUploadButtonText : uploadButtonText}
            isDisabled={isDisabled}
          />
        </motion.div>
      );
    }
  };

  const renderRemoveUploadButton = () => {
    if (hasRemoveUploadButton && upload.status !== 'uploading' && upload.url) {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="forms__form-field-upload-actions-remove"
        >
          <Button
            variant="ghost"
            size={buttonSize}
            onClick={handleRemoveUpload}
            text={removeUploadButtonText || i18n.t('remove')}
            isDisabled={isDisabled}
          />
        </motion.div>
      );
    }
  };

  const renderAltTextInput = () => {
    const schema = {
      alt_text: {
        type: 'Text',
        label: altTextLabelText,
        value: upload.alt_text || '',
      },
    };
    if (hasAltTextInput && upload.status !== 'uploading' && upload.url) {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="forms__form-field-upload-actions-alt-text"
        >
          <Form model={upload} onUpdate={handleAltTextChange} schema={schema} />
        </motion.div>
      );
    }
  };

  return (
    <div>
      <AnimatePresence exitBeforeEnter>
        {upload.status === 'uploading' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="forms__form-field-upload-actions-uploading"
          >
            <Progress
              value={upload.progress}
              shouldAnimate
              color="primary"
              className={styles.progress}
            />
          </motion.div>
        )}
        <div>
          <div className={styles.actions}>
            {renderReplaceUploadButton()}
            {renderRemoveUploadButton()}
          </div>
          <div className={styles.altText}>{renderAltTextInput()}</div>
        </div>
      </AnimatePresence>
    </div>
  );
};

FormFieldUploadActions.propTypes = {
  altTextLabel: PropTypes.string,
  buttonSize: PropTypes.string,
  fieldId: PropTypes.string.isRequired,
  handleAltTextChange: PropTypes.func.isRequired,
  handleFileUpload: PropTypes.func.isRequired,
  handleRemoveUpload: PropTypes.func.isRequired,
  hasAltTextInput: PropTypes.bool,
  hasIcon: PropTypes.bool,
  hasRemoveUploadButton: PropTypes.bool,
  hasReplaceUploadButton: PropTypes.bool.isRequired,
  icon: PropTypes.string,
  iconPosition: PropTypes.string,
  isDisabled: PropTypes.bool,
  permittedFormats: PropTypes.string,
  removeUploadButtonText: PropTypes.string,
  replaceUploadButtonText: PropTypes.string,
  upload: PropTypes.shape({
    alt_text: PropTypes.string,
    progress: PropTypes.number,
    status: PropTypes.string,
    url: PropTypes.string,
  }),
  uploadButtonText: PropTypes.string.isRequired,
};

export default React.memo(FormFieldUploadActions);
